import React, { useState, useEffect } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { messageError, messageToast } from '../../utils/define';
import { Get, Put } from '../../utils/httpProvider';
import { POSITION_API } from '../../utils/apiUrl';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

const schema = yup.object().shape({
    code: yup.string().required(messageError.emptyMessage),
    name: yup.string().required(messageError.emptyMessage),
    description: yup.string().nullable()
});

const PositionEdit = () => {
    const history = useHistory();
    const location = useLocation();
    const { id: routeId } = useParams();
    const queryId = new URLSearchParams(location.search).get('id');
    const id = routeId && routeId !== ':id' ? routeId : queryId;
    const [isLoading, setIsLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({
        code: '',
        name: '',
        description: '',
        isActive: true
    });

    useEffect(() => {
        const fetchItem = async () => {
            if (id) {
                setIsLoading(true);
                try {
                    const response = await Get(POSITION_API.GET_ITEM, id, null);
                    if (response) {
                        const item = response.data || response;
                        setInitialValues({
                            code: item.code || item.Code || '',
                            name: item.name || item.Name || '',
                            description: item.description || item.Description || '',
                            isActive: item.isActive !== undefined ? item.isActive : (item.IsActive !== undefined ? item.IsActive : true)
                        });
                    }
                } catch (error) {
                    toast.error('Lỗi khi lấy thông tin Vị trí!');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchItem();
    }, [id]);

    const handleUpdate = async (values) => {
        setIsLoading(true);
        try {
            const payload = {
                id: id,
                ...values,
                code: values.code.trim().toUpperCase(),
                name: values.name.trim()
            };
            await Put(POSITION_API.UPDATE, id, payload);
            toast.success(messageToast.messageToastUpdateSuccess);
            history.goBack();
        } catch (error) {
            toast.error(error?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card>
            <Card.Header as="h5">Sửa Vị trí</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize={true}
                    validationSchema={schema}
                    onSubmit={(values) => handleUpdate(values)}
                    initialValues={initialValues}
                >
                    {({ handleSubmit, handleChange, setFieldValue, values, errors }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Mã vị trí *
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        placeholder="Mã vị trí"
                                        type="text"
                                        name="code"
                                        value={values.code}
                                        onChange={(e) => {
                                            setFieldValue('code', e.target.value.toUpperCase());
                                        }}
                                        isInvalid={!!errors.code}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.code}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Tên vị trí *
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        placeholder="Tên vị trí"
                                        type="text"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        isInvalid={!!errors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Mô tả
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        placeholder="Mô tả"
                                        as="textarea"
                                        rows={3}
                                        name="description"
                                        value={values.description}
                                        onChange={handleChange}
                                        isInvalid={!!errors.description}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.description}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Trạng thái hoạt động
                                </Form.Label>
                                <Col sm="10" className="d-flex align-items-center">
                                    <Form.Check
                                        type="switch"
                                        id="isActive-switch"
                                        name="isActive"
                                        label={values.isActive ? "Đang hoạt động" : "Ngừng hoạt động"}
                                        checked={values.isActive}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button
                                            type="submit"
                                            theme="primary"
                                            disabled={isLoading}
                                        >
                                            Lưu thay đổi
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button
                                            onClick={() => history.goBack()}
                                            theme="secondary"
                                        >
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
                <ReactSpinner loadings={isLoading} />
            </Card.Body>
        </Card>
    );
};

export default PositionEdit;
